// src/components/HomeArticlesGrid.jsx
import React, { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useArticlesContext } from "../context/ArticlesContext";

/**
 * Grid component that can display either a random subset or all articles.
 *
 * Props:
 *  - count (number): How many articles to show (default: 3) if showAll === false
 *  - showAll (boolean): If true, display all articles instead of a random subset
 *  - articlesOverride (Array): If provided, this array of articles will be displayed
 *    instead of the normal logic.
 */
const HomeArticlesGrid = ({ count = 3, showAll = false, articlesOverride }) => {
  const navigate = useNavigate();
  const { articles } = useArticlesContext();

  // Decide which articles to show:
  // 1) If articlesOverride is given, use that
  // 2) Else if showAll === true, show all articles
  // 3) Otherwise pick a random subset
  const displayedArticles = useMemo(() => {
    if (articlesOverride && Array.isArray(articlesOverride)) {
      return articlesOverride;
    }
    if (articles.length === 0) return [];
    if (showAll) {
      return articles;
    } else {
      const shuffled = [...articles].sort(() => 0.5 - Math.random());
      return shuffled.slice(0, count);
    }
  }, [articles, count, showAll, articlesOverride]);

  /**
   * Transform each article so that .image becomes the "-low.jpg" version
   * if it ends with ".jpg", and keep the original in .imageOriginal.
   * Then, in the <img> tag, we use onError to fallback to the original image.
   */
  const processedArticles = useMemo(() => {
    return displayedArticles.map((art) => {
      const copy = { ...art };
      copy.imageOriginal = copy.image; // store the original
      if (copy.image && copy.image.endsWith(".jpg")) {
        copy.image = copy.image.replace(".jpg", "-low.jpg");
      }
      return copy;
    });
  }, [displayedArticles]);

  if (processedArticles.length === 0) {
    return null;
  }

  return (
    <section className="articles container">
      <h2>{showAll ? "All Articles" : "Recent Articles"}</h2>
      <div className="article-grid">
        {processedArticles.map((article) => (
          <article
            className="article-card"
            key={article.slug}
            onClick={() => navigate(`/articles/${article.slug}`)}
            style={{ cursor: "pointer" }}
          >
            {/* 
              Use the .image for the low-res version 
              and fallback to .imageOriginal if it fails
            */}
            <img
              src={article.image}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = article.imageOriginal;
              }}
              alt={article.title}
            />
            <div className="article-details">
              <h3>
                {/* Stop propagation to avoid double navigation history when the link is clicked */}
                <Link
                  to={`/articles/${article.slug}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  {article.title}
                </Link>
              </h3>
              <p>{article.excerpt}</p>
              {/* Same fix here for "Read More" */}
              <Link
                to={`/articles/${article.slug}`}
                className="read-more"
                onClick={(e) => e.stopPropagation()}
              >
                Read More
              </Link>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

export default HomeArticlesGrid;